/* eslint-disable react/no-multi-comp */
import React, { useContext, useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Col, Row } from 'reactstrap';
import { FiArrowLeft, FiEdit2, FiRefreshCw } from 'react-icons/fi';
import moment from 'moment';
import CardWithTitle from 'components/CardWithTitle';
import CustomTabs from 'components/CustomTabs';
import DateFormatter from 'components/DateFormatter';
import Loader from 'components/common/Loader';
import { ORDER_LIST } from 'utils/CommonGraphQL';
import { VENDOR_ORDER_STATUS } from 'utils/constants';
import OrderCardDetails from 'components/Drawers/components/OrderDetailsDrawer/components/OrderDetails/components/OrderCardDetails';
import OrderProductDetails from 'components/Drawers/components/OrderDetailsDrawer/components/OrderDetails/components/OrderProductDetails';
import PaymentDetails from 'components/Drawers/components/OrderDetailsDrawer/components/OrderDetails/components/PaymentDetails';
import DeliveryStatus from 'components/Drawers/components/OrderDetailsDrawer/components/OrderDetails/components/DeliverStatus';
import OrderRequest from 'components/Drawers/components/OrderDetailsDrawer/components/OrderDetails/components/OrderRequest';
import PolystoxApproval, {
  isPolystoxApprovalWaiting,
  readRejectedPolystoxApprovalIds,
} from 'components/Drawers/components/OrderDetailsDrawer/components/OrderDetails/components/PolystoxApproval';
import OrderBiddingDetails from 'components/Drawers/components/OrderDetailsDrawer/components/OrderDetails/components/OrderBiddingDetails';
import InvoiceList from 'components/Drawers/components/InvoiceListDetailsDrawer/components/InvoiceList';
import EditOrder from 'pages/Orders/components/EditOrderWrapper/components/EditOrder';
import { UPDATE_ORDER_STATUS } from 'pages/Orders/OrdersGraphQL';
import { validationSchema } from 'pages/Orders/constant';
// import { STAFF_LIST_QUERY } from 'pages/Staff/StaffgraphQL';
import { STATUS_UPDATED_SUCCESSFULLY } from 'components/common/FSBToastify/types';
import { authContext } from 'contexts/AuthContext';
import { axiosGetAPI } from 'services';
import './style.css';

const ORDER_DETAIL_TABS = [
  'Overview',
  'Invoice',
  'Payment',
  'Logistics',
  'Approval',
  'Timeline',
];

const STATUS_STEPS = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'IN_PROGRESS', label: 'In Progress' },
  { key: 'LOADING', label: 'Loading' },
  { key: 'DISPATCHED', label: 'Dispatched' },
  { key: 'DELIVERED', label: 'Delivered' },
];

const NEXT_STATUS_MAP = {
  PENDING: 'IN_PROGRESS',
};

const STATUS_REQUIRING_LOGISTICS_VALIDATION = [
  'DISPATCHED',
  'DELIVERED',
];

const getStatusLabel = (status) => {
  const statusFromSteps = STATUS_STEPS.find((step) => step.key === status);
  if (statusFromSteps) return statusFromSteps.label;

  return (
    VENDOR_ORDER_STATUS[status] ||
    String(status || 'Not Available')
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
  );
};

// const getUserFullName = (user) =>
//   [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

// const getRoleLabel = (roles = []) => {
//   const roleName = roles?.[0]?.name;
//   if (!roleName) return '';

//   return roleName
//     .toLowerCase()
//     .split('_')
//     .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
//     .join(' ');
// };

const getDefaultLogistic = (order, status) => ({
  id: null,
  order: order?.id,
  driverName: '',
  driverNumber: '',
  vehicle: '',
  vehicleNumber: '',
  transportName: '',
  transportLR: '',
  loadingCharges: null,
  otherCharges: null,
  freight: null,
  rateUnit: order?.rateUnit?.id,
  weightSlipDoc: [],
  lrDoc: [],
  vehicleNumberImage: [],
  loadingImage: [],
  orderLogisticStatus: status,
  loadingDate: null,
  dispatchDate: null,
  deliveryDate: null,
  loadingQty: '',
  loadingQtyUnit: order?.quantityUnit?.id || order?.quantityUnit,
  pickupAddress: {
    houseNumber: '',
    street: '',
    area: '',
    city: '',
    state: '',
    country: '',
    pinCode: '',
  },
});

const getValidationValues = (order, status) => {
  const orderLogistics = order?.orderLogistics?.length
    ? order.orderLogistics.map((logistic) => ({
      ...logistic,
      orderLogisticStatus: logistic?.orderLogisticStatus || status,
    }))
    : [getDefaultLogistic(order, status)];

  return {
    ...order,
    status,
    orderLogistics,
    rateUnit: {
      id: order?.rateUnit?.id,
      label: order?.rateUnit?.isoCurrencyCode,
    },
    quantityUnit: {
      id: order?.quantityUnit?.id || order?.quantityUnit,
      label:
        order?.quantityUnit?.label ||
        order?.quantityUnit?.isoCurrencyCode ||
        order?.quantityUnit,
    },
  };
};

const getTabsFromValidationErrors = (errors = []) => {
  const errorTabs = new Set();

  errors.forEach((error) => {
    const path = error?.path || '';

    if (path.startsWith('orderLogistics')) {
      errorTabs.add('Logistics');
      return;
    }

    if (path.startsWith('invoiceInfo')) {
      errorTabs.add('Invoice');
      return;
    }

    if (
      path.startsWith('paymentDetails') ||
      path.startsWith('paymentTransitions')
    ) {
      errorTabs.add('Payment');
      return;
    }

    errorTabs.add('Overview');
  });

  return [...errorTabs];
};

const DetailLine = ({ label, value }) => (
  <div className="order-detail-page__line">
    <span>{label}</span>
    <strong>{value || 'Not Available'}</strong>
  </div>
);

const INDIA_UTC_OFFSET = '+05:30';
const getIndiaMoment = (date) => moment(date).utcOffset(INDIA_UTC_OFFSET);

const getTimelineValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Not Available';
  }
  return value;
};

const getTimelineIcon = (action = '') => {
  const actionText = action.toLowerCase();
  return actionText.includes('created') || actionText.includes('entered')
    ? 'refresh'
    : 'edit';
};

const getTimelineTitle = (action = '') => action;

const getTimelineLines = (item) => {
  return [
    `Old: ${getTimelineValue(item.oldValue)}`,
    `New: ${getTimelineValue(item.newValue)}`,
  ];
};

const buildOrderTimelineHistory = (history) => {
  const sortedHistory = [...(history || [])].sort(
    (current, next) => next.createdAt - current.createdAt,
  );

  return sortedHistory.reduce((groups, item) => {
    const createdAtIndia = getIndiaMoment(item.createdAt * 1000);
    const dateKey = createdAtIndia.format('YYYY-MM-DD');
    const date = createdAtIndia.format('MMM D, YYYY');
    const currentGroup = groups.find((group) => group.dateKey === dateKey);
    const activity = {
      id: item.id,
      time: createdAtIndia.format('hh:mm A'),
      icon: getTimelineIcon(item.action),
      title: getTimelineTitle(item.action),
      lines: getTimelineLines(item),
      meta: `by ${item.updatedByName || 'System'} ${date}`,
    };

    if (currentGroup) {
      currentGroup.activities.push(activity);
      return groups;
    }

    return [
      ...groups,
      {
        dateKey,
        date,
        activities: [activity],
      },
    ];
  }, []);
};

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { auth } = useContext(authContext);

  const getInitialTab = () => {
    const queryTab = searchParams.get('tab');
    if (queryTab) {
      const match = ORDER_DETAIL_TABS.find(
        (t) => t.toLowerCase() === queryTab.toLowerCase(),
      );
      if (match) return match;
    }
    if (location.state?.tab && ORDER_DETAIL_TABS.includes(location.state.tab)) {
      return location.state.tab;
    }
    return 'Overview';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [tabValidationErrors, setTabValidationErrors] = useState([]);
  const [validateTimestamp, setValidateTimestamp] = useState(0);
  const [orderHistoryData, setOrderHistoryData] = useState([]);
  const [isOrderHistoryLoading, setIsOrderHistoryLoading] = useState(false);

  useEffect(() => {
    const queryTab = searchParams.get('tab');
    if (queryTab) {
      const match = ORDER_DETAIL_TABS.find(
        (t) => t.toLowerCase() === queryTab.toLowerCase(),
      );
      if (match && match !== activeTab) {
        setActiveTab(match);
      }
    } else if (location.state?.tab && ORDER_DETAIL_TABS.includes(location.state.tab)) {
      if (location.state.tab !== activeTab) {
        setActiveTab(location.state.tab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state]);

  useEffect(() => {
    const fetchOrderHistory = async () => {
      if (!orderId) return;
      setIsOrderHistoryLoading(true);
      const url = `${process.env.REACT_APP_REST_API_ENDPOINT}order/order-history/${orderId}`;
      const response = await axiosGetAPI(url, auth);
      if (response?.isSuccessful) {
        setOrderHistoryData(response.data?.data || []);
      }
      setIsOrderHistoryLoading(false);
    };
    if (activeTab === 'Timeline') {
      fetchOrderHistory();
    }
  }, [orderId, activeTab, auth]);

  const { data, loading, error, refetch } = useQuery(ORDER_LIST, {
    variables: {
      filter: [{ key: 'id', value: orderId, type: 'INT', op: 'EQ' }],
    },
    fetchPolicy: 'network-only',
  });

  const currentRow = data?.orders?.orders?.[0];

  // Conditional polling - when order is pending and awaiting approval, or when viewing Payment tab
  const shouldPoll =
    (currentRow?.status === 'PENDING' &&
      !['ORDER_APPROVED', 'ORDER_REJECTED', 'ORDER_APPROVAL_EXPIRED'].includes(
        currentRow?.approvalStatus,
      )) ||
    activeTab === 'Payment';

  // Live polling query - only active when shouldPoll is true
  const { data: liveData } = useQuery(ORDER_LIST, {
    variables: {
      filter: [{ key: 'id', value: orderId, type: 'INT', op: 'EQ' }],
    },
    fetchPolicy: 'network-only',
    pollInterval: shouldPoll ? 3000 : 0, // Poll every 3 seconds when pending
    skip: !shouldPoll,
  });

  // Use live data if available, otherwise use initial data
  const finalData = liveData || data;
  const finalCurrentRow = finalData?.orders?.orders?.[0];

  // The charge update endpoint persists freightValue/loadingValue without
  // updating isFreightChargeAccepted. For this workflow, saved charges mean
  // the order is now waiting for the buyer's freight charge acceptance.
  const hasFreightCharges =
    finalCurrentRow?.freightValue !== undefined &&
    finalCurrentRow?.freightValue !== null &&
    finalCurrentRow?.freightValue !== '' &&
    finalCurrentRow?.loadingValue !== undefined &&
    finalCurrentRow?.loadingValue !== null &&
    finalCurrentRow?.loadingValue !== '';
  const approvalOrder = hasFreightCharges
    ? { ...finalCurrentRow, isFreightChargeAccepted: true }
    : finalCurrentRow;

  const [isPolystoxApprovalRejected, setIsPolystoxApprovalRejected] =
    useState(false);
  const [isPolystoxApprovalAccepted, setIsPolystoxApprovalAccepted] =
    useState(false);

  useEffect(() => {
    setIsPolystoxApprovalAccepted(false);
    setIsPolystoxApprovalRejected(
      orderId ? readRejectedPolystoxApprovalIds().includes(String(orderId)) : false,
    );
  }, [orderId]);

  useEffect(() => {
    const handlePolystoxUpdate = () => {
      if (orderId) {
        setIsPolystoxApprovalRejected(
          readRejectedPolystoxApprovalIds().includes(String(orderId)),
        );
      }
    };
    window.addEventListener('orderCreated', handlePolystoxUpdate);
    return () => {
      window.removeEventListener('orderCreated', handlePolystoxUpdate);
    };
  }, [orderId]);

  const hasPolystoxApprovalWaiting = useMemo(() => {
    if (isPolystoxApprovalAccepted || isPolystoxApprovalRejected) {
      return false;
    }
    return isPolystoxApprovalWaiting(approvalOrder);
  }, [approvalOrder, isPolystoxApprovalAccepted, isPolystoxApprovalRejected]);

  const activeErrorTabs = useMemo(() => {
    const errorSet = new Set(tabValidationErrors);
    if (hasPolystoxApprovalWaiting) {
      errorSet.add('Approval');
    }
    return [...errorSet];
  }, [tabValidationErrors, hasPolystoxApprovalWaiting]);

  // const approverUserId = finalCurrentRow?.updatedByUserId;

  // const { data: approverData } = useQuery(STAFF_LIST_QUERY, {
  //   variables: {
  //     filter: [{ key: 'id', value: approverUserId, type: 'INT', op: 'EQ' }],
  //     pageNo: 1,
  //     pageSize: 1,
  //     pagination: false,
  //   },
  //   fetchPolicy: 'network-only',
  //   skip: !approverUserId,
  // });

  // const approverUser = approverData?.usersFilter?.users?.[0];
  // const approverNameFromUser = getUserFullName(approverUser);
  // const approverRoleFromUser = getRoleLabel(approverUser?.roles);
  // const loggedInUserId = auth?.data?.userId || auth?.data?.id;
  // const loggedInUserName =
  //   `${auth?.data?.firstName || ''} ${auth?.data?.lastName || ''}`.trim();
  // const loggedInUserRole = getRoleLabel(auth?.data?.roles);


  const currentStatus = finalCurrentRow?.status || '';
  const canApprove = currentStatus === 'PENDING';
  const autoSelectedStatus = NEXT_STATUS_MAP[currentStatus] || '';
  const selectedStatusKey = canApprove
    ? selectedStatus || autoSelectedStatus
    : '';
  const selectedStatusLabel = getStatusLabel(selectedStatusKey);
  const currentStatusIndex = STATUS_STEPS.findIndex(
    (step) => step.key === currentStatus,
  );

  const [updateOrderStatus, { loading: updatingStatus }] = useMutation(
    UPDATE_ORDER_STATUS,
    {
      onCompleted: () => {
        toast.success(STATUS_UPDATED_SUCCESSFULLY);
        setSelectedStatus('');
        setTabValidationErrors([]);
        refetch();
      },
      onError: ({ graphQLErrors = [] }) => {
        graphQLErrors.forEach(({ exception, message }) => {
          toast.error(exception?.message || message);
        });
      },
    },
  );

  const paymentProps = useMemo(() => {
    if (!finalCurrentRow) return {};

    const {
      paymentTransitions,
      gstAmount,
      tdsValue,
      tdsAmount,
      prepayment,
      convenienceAmount,
      total,
      subtotal,
      gstValue,
      tcsValue,
      tcsAmount,
      paymentTerm,
      rateUnit,
      rate,
      quantity,
      insuranceAmount,
      isFreeTransitInsuranceIncluded,
      availInsurance,
    } = finalCurrentRow;

    return {
      paymentTransitions,
      gstAmount,
      gstValue,
      tdsValue,
      tdsAmount,
      tcsValue,
      tcsAmount,
      prepayment,
      convenienceAmount,
      total,
      subtotal,
      paymentTerm,
      rateUnit,
      rate,
      quantity,
      insuranceAmount,
      isFreeTransitInsuranceIncluded,
      availInsurance,
    };
  }, [finalCurrentRow]);

  const renderEditableOrderSections = (sections) => (
    <div className="order-detail-page__embedded-form">
      <EditOrder
        orderInitialValues={finalCurrentRow}
        onCloseOrderPopup={refetch}
        onOrderUpdated={refetch}
        sections={sections}
        validateTimestamp={validateTimestamp}
      />
    </div>
  );

  const handleStatusUpdate = async () => {
    if (
      !finalCurrentRow?.id ||
      !selectedStatusKey ||
      selectedStatusKey === currentStatus
    ) {
      return;
    }

    if (STATUS_REQUIRING_LOGISTICS_VALIDATION.includes(selectedStatusKey)) {
      try {
        const validationValues = getValidationValues(
          finalCurrentRow,
          selectedStatusKey,
        );

        await validationSchema.validate(validationValues, {
          abortEarly: false,
          context: validationValues,
        });
        setTabValidationErrors([]);
      } catch (validationError) {
        setValidateTimestamp(Date.now());
        const validationErrors = validationError?.inner?.length
          ? validationError.inner
          : [validationError];
        const errorTabs = getTabsFromValidationErrors(validationErrors);
        const firstErrorMessage =
          validationErrors.find((item) => item?.message)?.message ||
          'Please complete required order details before approval.';

        setTabValidationErrors(errorTabs);
        if (errorTabs[0]) setActiveTab(errorTabs[0]);
        toast.error(firstErrorMessage);
        return;
      }
    }

    const deliveredQty = Math.round(
      Number(finalCurrentRow?.quantity || currentRow?.quantity || 0),
    );

    if (
      selectedStatusKey === 'DELIVERED' &&
      (!deliveredQty || deliveredQty <= 0)
    ) {
      toast.error('Please enter valid delivered quantity');
      return;
    }

    await updateOrderStatus({
      variables: {
        id: finalCurrentRow.id,
        status: selectedStatusKey,
        deliveredQuantity:
          selectedStatusKey === 'DELIVERED'
            ? deliveredQty
            : null,
      },
    });
  };

  const renderTabContent = () => {
    if (activeTab === 'Invoice') {
      return (
        <>
          <CardWithTitle title="Invoice List">
            <InvoiceList orderId={orderId} />
          </CardWithTitle>
          {renderEditableOrderSections(['pipo'])}
        </>
      );
    }

    if (activeTab === 'Payment') {
      return (
        <>
          <PaymentDetails {...paymentProps} />
          {renderEditableOrderSections(['commission', 'payment'])}
        </>
      );
    }

    if (activeTab === 'Logistics') {
      const isLogisticSelf = Boolean(
        finalCurrentRow?.isLogisticSelf === true ||
        finalCurrentRow?.isLogisticSelf === 'true'
      );
      const logisticsSections = isLogisticSelf
        ? ['logistics']
        : ['charges', 'logistics'];

      return (
        <>
          <CardWithTitle title="Delivery Status">
            {finalCurrentRow?.orderLogistics?.length ? (
              finalCurrentRow.orderLogistics.map((item) => (
                <DeliveryStatus logistic={item} key={item.id} />
              ))
            ) : (
              <span className="d-block mb-2">
                {finalCurrentRow?.status
                  ? VENDOR_ORDER_STATUS[finalCurrentRow.status]
                  : 'Not Available'}
              </span>
            )}
          </CardWithTitle>
          {renderEditableOrderSections(logisticsSections)}
        </>
      );
    }

    if (activeTab === 'Approval') {
      return (
        <>
          <OrderRequest order={finalCurrentRow} />
          <PolystoxApproval
            order={approvalOrder}
            onCompleted={refetch}
            onAccept={() => setIsPolystoxApprovalAccepted(true)}
            onReject={() => setIsPolystoxApprovalRejected(true)}
          />
          {finalCurrentRow?.bid && <OrderBiddingDetails bid={finalCurrentRow.bid} />}
          {!finalCurrentRow?.bid && (
            <CardWithTitle title="Audit Trail">
              <DetailLine
                label="Approval Status"
                value={finalCurrentRow?.approvalStatus}
              />
              <DetailLine label="Order Status" value={finalCurrentRow?.status} />
              <DetailLine
                label="Payment Status"
                value={finalCurrentRow?.paymentStatus}
              />
            </CardWithTitle>
          )}
        </>
      );
    }

    if (activeTab === 'Timeline') {
      const orderTimelineHistory = buildOrderTimelineHistory(orderHistoryData);

      return (
        <CardWithTitle title="Order Timeline" className="vendor-timeline-card">
          <div className="vendor-timeline">
            {(() => {
              if (isOrderHistoryLoading) {
                return <div className="mb-2">Loading...</div>;
              }
              if (orderTimelineHistory.length === 0) {
                return <div className="mb-2">Not Available</div>;
              }
              return orderTimelineHistory.map((group) => (
                <div className="vendor-timeline-group" key={group.dateKey}>
                  <span className="vendor-timeline-date">{group.date}</span>
                  {group.activities.map((activity) => (
                    <div className="vendor-timeline-item" key={activity.id}>
                      <span className="vendor-timeline-time">{activity.time}</span>
                      <span className="vendor-timeline-icon">
                        {activity.icon === 'refresh' ? (
                          <FiRefreshCw size={13} />
                        ) : (
                          <FiEdit2 size={13} />
                        )}
                      </span>
                      <div className="vendor-timeline-content">
                        <p className="vendor-timeline-title">{activity.title}</p>
                        {activity.lines?.map((line) => (
                          <p className="vendor-timeline-line" key={line}>
                            {line}
                          </p>
                        ))}
                        <p className="vendor-timeline-meta">{activity.meta}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        </CardWithTitle>
      );
    }

    return (
      <>
        <Row>
          <Col lg={8}>
            <OrderCardDetails currentRow={finalCurrentRow} />
          </Col>
          <Col lg={4}>
            <OrderProductDetails currentRow={finalCurrentRow} />
          </Col>
        </Row>
        {renderEditableOrderSections(['summary', 'address'])}
      </>
    );
  };

  if (loading) return <Loader />;

  if (error) {
    return (
      <Card className="border-0 card-box page-card-box">
        <CardBody>There is an error on this page: {error.message}</CardBody>
      </Card>
    );
  }

  if (!finalCurrentRow) {
    return (
      <Card className="border-0 card-box page-card-box">
        <CardBody>Orders not available</CardBody>
      </Card>
    );
  }

  return (
    <div className="order-detail-page">
      <div className="order-detail-page__topbar">
        <Button
          color="link"
          className="order-detail-page__back"
          onClick={() => navigate('/orders')}
        >
          <FiArrowLeft size={18} />
          Back
        </Button>
        <Badge color="success" className="order-detail-page__top-status">
          {getStatusLabel(finalCurrentRow?.status)}
        </Badge>
      </div>

      <div className="order-detail-page__header">
        <div className="order-detail-page__title-block">
          <h4>Order #{finalCurrentRow.id}</h4>
          {finalCurrentRow?.createdAt && (
            <div className="order-detail-page__meta">
              <DateFormatter
                dateTime={finalCurrentRow.createdAt}
                className="order-detail-page__meta-time"
              />
            </div>
          )}
        </div>
        <div className="order-detail-page__summary">
          <DetailLine label="Grade" value={finalCurrentRow?.grade?.name} />
          <DetailLine label="Buyer" value={finalCurrentRow?.buyer?.entityName} />
          <DetailLine label="Seller" value={finalCurrentRow?.seller?.entityName} />
        </div>
      </div>

      <div className="order-detail-page__body-grid">
        <section className="order-detail-page__workflow-card">
          <div className="order-detail-page__section-title">
            <span className="order-detail-page__section-icon" />
            <h5>Order Status Workflow</h5>
          </div>
          <div className="order-detail-page__timeline">
            {STATUS_STEPS.map((step, index) => {
              const isCurrent = step.key === currentStatus;
              const isSelected =
                Boolean(selectedStatusKey) && step.key === selectedStatusKey;
              const isCompleted =
                currentStatusIndex >= 0 && index < currentStatusIndex;
              const isApprovable =
                currentStatus === 'PENDING' && step.key === 'IN_PROGRESS';

              return (
                <div
                  className={`order-detail-page__step ${isCurrent ? 'is-current' : ''
                    } ${isSelected && isApprovable ? 'is-selected' : ''} ${isCompleted ? 'is-completed' : ''
                    }`}
                  key={step.key}
                >
                  <span className="order-detail-page__step-check" />
                  <div>
                    <strong>{step.label}</strong>
                    <p>
                      {isCurrent
                        ? `Order #${finalCurrentRow.id} is in ${step.label}`
                        : `Update order to ${step.label}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="order-detail-page__tabs-card">
          <CustomTabs
            tabs={ORDER_DETAIL_TABS}
            selectedTab={activeTab}
            onTabChange={setActiveTab}
            errorTabs={activeErrorTabs}
          />
          <div className="order-detail-page__content">{renderTabContent()}</div>
        </section>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
